// DeliveryLocation.js – Production Ready (Amazon-style)

import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import CheckoutBreadcrumbs from "./CheckoutBreadcrumbs";
import api from "../api/axios";
import { getCheckout, setCheckout } from "../utils/buyNowStorage";


export default function DeliveryLocation() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [user, setUser] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [newAddress, setNewAddress] = useState({
    addressLine1: "",
    city: "",
    state: "",
    pincode: "",
  });

  const [openMap, setOpenMap] = useState(false);

  const checkout = getCheckout();

  /* ---------------- LOAD USER + ADDRESSES ---------------- */
  useEffect(() => {
    if (!id) return;

    const loadProduct = async () => {
      try {
        const res = await api.get(`/reels/${id}`);
        const product = res.data;

        setCheckout({
          mode: "BUY_NOW",
          items: [
            {
              id: product._id || product.id,
              title: product.title,
              price: Number(product.price) || 0,
              imageUrl: product.images?.[0]?.thumbnailKey || product.images?.[0] || product.imageUrl,
              qty: 1,
              vendorId: product.vendorId,
              vendorName: product.vendorName,
            },
          ],
        });
      } catch (err) {
        console.error("Failed to load product", err);
      }
    };

    loadProduct();
  }, [id]);
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser?.username) return;

    setUser(storedUser);

    api
      .get(`/users/${storedUser.username}/addresses`)
      .then((res) => {
        const addresses = res.data.addresses || [];
        setSavedAddresses(addresses);

        const defaultAddr = addresses.find((a) => a.isDefault);
        if (defaultAddr) setSelectedAddressId(defaultAddr._id);
      })
      .catch(() => setSavedAddresses([]));
  }, []);

  // setCheckout({
  //     mode: "BUY_NOW",
  //     items: [
  //       {
  //         id: product._id,
  //         title: product.title,
  //         price: sanitizePrice(product.price),
  //         imageUrl: product.images[0]?.thumbnailKey || product.images[0],
  //         qty: finalQty,
  //         vendorId: product.vendorId,
  //         vendorName: product.vendorName,
  //       },
  //     ],
  //   });

  /* ---------------- CONTINUE ---------------- */
  const handleContinue = () => {
    if (!selectedAddressId) {
      alert("Please select a delivery address");
      return;
    }

    if (checkout) {
      setCheckout({ ...checkout, addressId: selectedAddressId });
    }

    navigate("/order-summary", {
      state: { addressId: selectedAddressId },
    });
  };

  /* ---------------- SAVE ADDRESS ---------------- */
  const handleSaveAddress = async () => {
    const { addressLine1, city, state, pincode } = newAddress;
    if (!addressLine1 || !city || !state || !pincode) {
      alert("Please fill all address fields");
      return;
    }

    try {
      const res = await api.post(
        `/users/${user.username}/addresses`,
        newAddress
      );
      setSavedAddresses(res.data.addresses || []);
      setOpenAdd(false);
      setNewAddress({
        addressLine1: "",
        city: "",
        state: "",
        pincode: "",
      });
    } catch {
      alert("Failed to save address");
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <Box sx={{ maxWidth: 720, mx: "auto", px: 2, py: 3 }}>
      <CheckoutBreadcrumbs active={0} />

      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Select a delivery address
      </Typography>

      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        {savedAddresses.length === 0 ? (
          <Typography color="text.secondary">
            No saved addresses found.
          </Typography>
        ) : (
          <RadioGroup
            value={selectedAddressId}
            onChange={(e) => setSelectedAddressId(e.target.value)}
          >
            {savedAddresses.map((addr) => (
              <Paper key={addr._id} sx={{ p: 2, mb: 2 }}>
                <FormControlLabel
                  value={addr._id}
                  control={<Radio />}
                  sx={{ alignItems: "flex-start" }}
                  label={
                    <Box>
                      <Typography fontWeight={600}>
                        {user?.firstName} {user?.lastName}
                      </Typography>
                      <Typography fontSize={14}>
                        {addr.addressLine1}, {addr.city}, {addr.state}{" "}
                        {addr.pincode}
                      </Typography>
                    </Box>
                  }
                />
              </Paper>
            ))}
          </RadioGroup>
        )}

        <Button
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
          onClick={() => setOpenAdd(true)}
        >
          ➕ Add new address
        </Button>
      </Paper>

      <Button
        fullWidth
        variant="outlined"
        sx={{ mb: 2 }}
        onClick={() => setOpenMap(true)}
      >
        📍 Pick location on Google Maps
      </Button>

      <Divider sx={{ my: 2 }} />

      <Button fullWidth variant="contained" onClick={handleContinue}>
        Deliver to this address
      </Button>

      {/* ---------------- ADD ADDRESS ---------------- */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth>
        <DialogContent>
          <TextField
            label="Address Line 1"
            fullWidth
            sx={{ mb: 2 }}
            value={newAddress.addressLine1}
            onChange={(e) =>
              setNewAddress({ ...newAddress, addressLine1: e.target.value })
            }
          />
          <TextField
            label="City"
            fullWidth
            sx={{ mb: 2 }}
            value={newAddress.city}
            onChange={(e) =>
              setNewAddress({ ...newAddress, city: e.target.value })
            }
          />
          <TextField
            label="State"
            fullWidth
            sx={{ mb: 2 }}
            value={newAddress.state}
            onChange={(e) =>
              setNewAddress({ ...newAddress, state: e.target.value })
            }
          />
          <TextField
            label="Pincode"
            fullWidth
            value={newAddress.pincode}
            onChange={(e) =>
              setNewAddress({ ...newAddress, pincode: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAddress}>
            Save Address
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- MAP PICKER ---------------- */}
      <Dialog open={openMap} onClose={() => setOpenMap(false)} fullWidth>
        <DialogContent>
          <iframe
            title="google-map"
            width="100%"
            height="350"
            style={{ borderRadius: 12 }}
            loading="lazy"
            src="https://maps.google.com/maps?q=India&t=&z=13&output=embed"
          />
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => setOpenMap(false)}
          >
            Confirm Location
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
